import * as React from 'react'

import { toast } from '@buffer-mono/popcorn'

import type { CommentData } from '../context/CommentContext'

import { useCommentThread } from '../../hooks/comments/useCommentThread'

type UseLoadCommentContextResult = {
  loadContext: (commentId: string) => Promise<CommentData[] | null>
  isLoading: boolean
  showSpinner: boolean
}

export const useLoadCommentContext = (
  spinnerDelay = 300,
): UseLoadCommentContextResult => {
  const { getCommentThread } = useCommentThread()
  const [isLoading, setIsLoading] = React.useState(false)
  const [showSpinner, setShowSpinner] = React.useState(false)
  const spinnerTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const isMountedRef = React.useRef(true)

  React.useEffect(() => {
    // Cleanup: clear timeout and mark as unmounted
    return (): void => {
      isMountedRef.current = false
      if (spinnerTimeoutRef.current) {
        clearTimeout(spinnerTimeoutRef.current)
      }
    }
  }, [])

  const loadContext = React.useCallback(
    async (commentId: string): Promise<CommentData[] | null> => {
      setIsLoading(true)

      // Only show spinner if loading takes longer than the specified delay
      spinnerTimeoutRef.current = setTimeout(() => {
        setShowSpinner(true)
      }, spinnerDelay)

      try {
        const result = await getCommentThread(commentId)
        return result.context
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching comment context:', error)
        toast.error('Failed to load comment context')
        return null
      } finally {
        if (spinnerTimeoutRef.current) {
          clearTimeout(spinnerTimeoutRef.current)
          spinnerTimeoutRef.current = null
        }
        // Only update state if component is still mounted
        if (isMountedRef.current) {
          setIsLoading(false)
          setShowSpinner(false)
        }
      }
    },
    [getCommentThread, spinnerDelay],
  )

  return { loadContext, isLoading, showSpinner }
}
